// The AE view's readings (GRO-360, reworked 20 Oct): the deal amount, relative days, the summary
// counts and the panel's suggested next actions. What an AE works from is the deal and the
// meetings around it, so nothing here reads the strategist's why-now, research or bank state.
//
// Pure and imported by `node --test`.

import type { BookAccount } from '../api/types.ts';
import { dealAmount, openOpportunities, usageFact } from '../sidebar/panelFacts.ts';
import type { StripFact } from '../sidebar/panelFacts.ts';
import { formatMoney } from '../lib/format.ts';
import { isStageOne, leadDeal, nextCloseDay, nextMeetingDay } from './aeSections.ts';

export { dealAmount };

// Every deal reading on an AE row or strip is the lead deal's (`leadDeal`, the furthest-along open
// one): its stage, amount, close date and next step, so a row never mixes two deals.

/** The lead deal's figure, or null with no open deal or no figure on it. */
export function leadDealAmount(row: BookAccount): number | null {
  const deal = leadDeal(row);
  return deal ? dealAmount(deal) : null;
}

/** The lead deal's close date. */
export function leadDealCloseDay(row: BookAccount): string | null {
  return leadDeal(row)?.close_date ?? null;
}

/** The lead deal's first meeting date, or null with no open deal or none set in Salesforce. */
export function leadDealFirstMeetingDay(row: BookAccount): string | null {
  return leadDeal(row)?.first_meeting_on ?? null;
}

/** Open deals beyond the lead one, for the "+N more open" note. */
export function moreOpenDeals(row: BookAccount): number {
  return Math.max(openOpportunities(row).length - 1, 0);
}

/** Whole days from `today` to `day`: negative in the past, positive ahead. ISO days, UTC. */
export function daysFrom(day: string, today: string): number {
  const dayTime = new Date(day + 'T00:00:00Z').getTime();
  const todayTime = new Date(today + 'T00:00:00Z').getTime();
  return Math.round((dayTime - todayTime) / 864e5);
}

/** "today", "3d ago", "in 5d", with months past 60 days. Short words only: the day columns are narrow. */
export function relativeDay(day: string, today: string): string {
  const offset = daysFrom(day, today);
  if (offset === 0) return 'today';
  const distance = Math.abs(offset);
  const amount = distance >= 60 ? `${Math.round(distance / 30.44)}mo` : `${distance}d`;
  return offset < 0 ? `${amount} ago` : `in ${amount}`;
}

/** "Oct 24", or "Jan 4 '27" when the year is not this one. */
export function shortDay(day: string, today: string): string {
  const date = new Date(day + 'T00:00:00Z');
  const sameYear = day.slice(0, 4) === today.slice(0, 4);
  const monthAndDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return sameYear ? monthAndDay : `${monthAndDay} '${day.slice(2, 4)}`;
}

// --- The summary line ---------------------------------------------------------------------

export interface AeCount {
  label: string;
  value: number;
  title: string;
}

// How far ahead "closing soon" looks, and how long without an AE touch counts as quiet.
export const CLOSING_SOON_DAYS = 30;

/** The counts above the AE table, each with a label that says what it counts. */
export function aeCounts(rows: BookAccount[], today: string): AeCount[] {
  const withOpenDeal = rows.filter(row => openOpportunities(row).length > 0).length;
  const withMeeting = rows.filter(row => nextMeetingDay(row, today) !== null).length;
  const closingSoon = rows.filter(row => {
    const close = nextCloseDay(row);
    if (!close) return false;
    const offset = daysFrom(close, today);
    return offset >= 0 && offset <= CLOSING_SOON_DAYS;
  }).length;
  const pastClose = rows.filter(row => {
    const close = nextCloseDay(row);
    return Boolean(close) && daysFrom(close!, today) < 0;
  }).length;
  const replied = rows.filter(row => row.responses > 0).length;
  return [
    { label: 'with an open opportunity', value: withOpenDeal, title: 'Accounts with at least one open Salesforce opportunity.' },
    { label: 'with a meeting booked', value: withMeeting, title: 'Accounts with a meeting still to come, yours or the SDR’s.' },
    { label: `closing in ${CLOSING_SOON_DAYS} days`, value: closingSoon, title: `An open opportunity's close date falls in the next ${CLOSING_SOON_DAYS} days.` },
    { label: 'past their close date', value: pastClose, title: 'An open opportunity whose close date has already gone by.' },
    { label: 'replied to the SDR', value: replied, title: 'Somebody at the account has replied to the SDR’s outreach.' },
  ];
}

// --- The panel's suggestions --------------------------------------------------------------

export type AeProposalKind = 'past_close' | 'next_step' | 'no_next_step' | 'book_meeting' | 'prepare_meeting' | 'quiet';

export interface AeProposal {
  kind: AeProposalKind;
  title: string;
  detail: string;
}

// An open deal with no AE touch for this long is worth a nudge.
export const AE_QUIET_DAYS = 21;
// A meeting this close is worth preparing for.
export const PREPARE_MEETING_DAYS = 2;

/**
 * What the AE might do next on this account, from its open deal and meetings, most pressing
 * first. Only suggestions an AE acts on in Salesforce or their calendar; none of the strategist's
 * outreach, research or bank actions. Empty when nothing fits, rather than borrowed strategist ones.
 */
export function aeProposals(row: BookAccount, today: string): AeProposal[] {
  const deal = leadDeal(row);
  const proposals: AeProposal[] = [];
  const meeting = nextMeetingDay(row, today);

  // The rest are about an open deal; with none, at most a meeting to prepare for.
  if (!deal) {
    if (meeting && daysFrom(meeting, today) <= PREPARE_MEETING_DAYS) {
      proposals.push({ kind: 'prepare_meeting', title: 'Prepare for the meeting', detail: `Meeting ${relativeDay(meeting, today)} (${meeting}).` });
    }
    return proposals;
  }
  const dealName = deal.name || 'the open opportunity';

  // A close date already gone by is the most pressing: the forecast is wrong until it moves.
  const close = deal.close_date;
  if (close && daysFrom(close, today) < 0) {
    proposals.push({
      kind: 'past_close', title: 'Update the close date',
      detail: `${dealName} was due to close ${relativeDay(close, today)} (${close}). Move the date or close it out in Salesforce.`,
    });
  }

  // The rep's own next step, or a prompt to write one. The step's text is on the deal card
  // below, so it is pointed to rather than repeated.
  if (deal.next_step) {
    proposals.push({ kind: 'next_step', title: 'Follow up on the next step', detail: `The next step on ${dealName} is under Open opportunity.` });
  } else {
    proposals.push({ kind: 'no_next_step', title: 'Add a next step', detail: `${dealName} has no next step in Salesforce.` });
  }

  // A meeting on the calendar, or the lack of one.
  if (meeting && daysFrom(meeting, today) <= PREPARE_MEETING_DAYS) {
    proposals.push({ kind: 'prepare_meeting', title: 'Prepare for the meeting', detail: `Meeting ${relativeDay(meeting, today)} (${meeting}).` });
  } else if (!meeting) {
    proposals.push({ kind: 'book_meeting', title: 'Book the next meeting', detail: `Nothing is on the calendar for ${dealName}.` });
  }

  // An open deal the AE has gone quiet on.
  const lastAeTouch = row.last_touch_by_ae ?? null;
  if (!lastAeTouch) {
    proposals.push({ kind: 'quiet', title: 'Log your last touch', detail: 'No activity of yours is logged on this account in Salesforce.' });
  } else if (-daysFrom(lastAeTouch, today) >= AE_QUIET_DAYS) {
    proposals.push({ kind: 'quiet', title: 'Check in', detail: `Your last logged touch was ${relativeDay(lastAeTouch, today)} (${lastAeTouch}).` });
  }
  return proposals;
}

// --- The panel's header strip ---------------------------------------------------------------

/**
 * The facts above every panel tab for an AE (`lib/roleViews.ts` picks it): the lead deal's stage as
 * the deal names it, its amount and close date, the next meeting, the SDR who owns outbound, usage.
 * Not the AE owner (the reader) or the SDR's last send.
 */
export function aeFactStrip(row: BookAccount, today: string): StripFact[] {
  const deal = leadDeal(row);
  const more = moreOpenDeals(row);
  const amount = leadDealAmount(row);
  const close = leadDealCloseDay(row);
  const meeting = nextMeetingDay(row, today);
  const overdue = close != null && daysFrom(close, today) < 0;
  return [
    { label: 'Stage', value: deal?.stage || 'No open opportunity', note: more > 0 ? `+${more} more open` : null, tone: null, valueTone: isStageOne(deal?.stage) ? 'warn' : null },
    { label: 'Amount', value: amount == null ? '—' : formatMoney(amount)!, note: null, tone: null },
    { label: 'Close date', value: close ? relativeDay(close, today) : '—', note: overdue ? 'overdue' : null, tone: overdue ? 'down' : null, title: close },
    { label: 'Next meeting', value: meeting ? relativeDay(meeting, today) : 'None booked', note: null, tone: null, title: meeting },
    { label: 'SDR owner', value: row.sdr_owner || 'Unassigned', note: null, tone: null },
    usageFact(row),
  ];
}
